import { useEffect, useState, useMemo } from "react";
import { createUseStyles } from "react-jss";
import { shallowEqual, useSelector } from "react-redux";
import {
  EuiFlexGroup,
  EuiFlexItem,
  EuiTab,
  EuiTabs,
  EuiText,
  EuiIcon,
  EuiToolTip,
} from "@elastic/eui";
import moment from "moment-timezone";
import { globalFilterSelector } from "reduxStore/globalFilter/globalFilter.selectors";
import { Flyout } from "components/Flyout/Flyout";
import { user_notification_styles } from "styles/pages/layout/notification_styles";
import ClientService from "services/ClientService/Clients";
import IconButton from "components/Buttons/IconButton";
import Button from "components/Buttons/Button";

const useStyles = createUseStyles(user_notification_styles);

export const NotificationsFlyout = ({ onClose, notifications, setNotifications }) => {
  const classes = useStyles();
  const { clientId } = useSelector(globalFilterSelector, shallowEqual);

  const unread = useMemo(
    () => notifications?.filter((n) => n?.attributes?.isRead === false),
    [notifications]
  );

  const tabs = useMemo(
    () => [
      {
        id: "all",
        name: "All",
      },
      {
        id: "unread",
        name: `Unread (${unread?.length || 0})`,
      },
    ],
    [unread]
  );

  const [selectedTabId, setSelectedTabId] = useState(tabs[0].id);

  const renderTabs = tabs.map((tab, index) => (
    <EuiTab
      className={classes.tab}
      isSelected={tab.id === selectedTabId}
      css={tab.id === selectedTabId ? { boxShadow: "inset 0 -2px 0 var(--button-bg)" } : {}}
      key={index}
      onClick={() => onSelectedTabChanged(tab.id)}>
      {tab.name}
    </EuiTab>
  ));

  const onSelectedTabChanged = (id) => {
    return setSelectedTabId(id);
  };

  const noNotifications = (
    <EuiFlexItem>
      <EuiText>
        <p className={classes.message}>No notifications at the moment.</p>
      </EuiText>
    </EuiFlexItem>
  );

  const getIconByPriority = (priority: "Low" | "Medium" | "High") => {
    switch (priority) {
      case "High":
        return {
          type: "warning",
          color: "#ff5c5c",
          backgroundColor: "rgba(189, 39, 30, 0.1)",
        };
      case "Medium":
        return {
          type: "bell",
          color: "#D97706",
          backgroundColor: "rgba(217, 119, 6, 0.1)",
        };
      case "Low":
        return {
          type: "info",
          color: "#007E77",
          backgroundColor: "rgba(0, 126, 119, 0.1)",
        };
      default:
        return {
          type: "warning",
          color: "#ff5c5c",
          backgroundColor: "rgba(189, 39, 30, 0.1)",
        };
    }
  };

  const getRelativeTime = (dateString: string) => {
    const now = moment();
    const utcDate = moment.utc(dateString);
    const localDate = utcDate.local();

    if (!localDate.isValid()) {
      return "Unknown time";
    }

    const diffInDays = now.diff(localDate, "days");
    const diffInHours = now.diff(localDate, "hours");
    const diffInMinutes = now.diff(localDate, "minutes");

    if (diffInDays === 0) {
      if (diffInHours === 0) {
        if (diffInMinutes === 0) {
          return "just now";
        }
        return diffInMinutes === 1 ? "1 minute ago" : `${diffInMinutes} minutes ago`;
      }
      return diffInHours === 1 ? "1 hour ago" : `${diffInHours} hours ago`;
    } else if (diffInDays === 1) {
      return "1 day ago";
    } else if (diffInDays < 7) {
      return `${diffInDays} days ago`;
    } else if (diffInDays < 14) {
      return "1 week ago";
    } else if (diffInDays < 30) {
      const weeks = Math.floor(diffInDays / 7);
      return `${weeks} weeks ago`;
    } else if (diffInDays < 60) {
      return "1 month ago";
    } else {
      const months = Math.floor(diffInDays / 30);
      return `${months} months ago`;
    }
  };

  const renderNotification = (i, idx) => {
    const iconProps = getIconByPriority(i.priority);
    return (
      <EuiFlexItem key={i.id}>
        <EuiFlexGroup gutterSize="m" alignItems="center">
          <EuiFlexItem grow={false}>
            <div
              style={{
                borderRadius: "50%",
                backgroundColor: iconProps.backgroundColor,
                width: "40px",
                height: "40px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}>
              <EuiIcon type={iconProps.type} size="m" color={iconProps.color} />
            </div>
          </EuiFlexItem>

          <EuiFlexItem>
            <EuiFlexGroup direction="column" gutterSize="xs">
              {/* Category and time */}
              <EuiFlexItem>
                <EuiText size="s">
                  <p style={{ marginBottom: "4px", fontWeight: 500 }}>
                    {i.category}{" "}
                    <span style={{ color: "#7d7e80", fontWeight: 400 }}>
                      {getRelativeTime(i.createDateTime)}
                    </span>
                  </p>
                </EuiText>
              </EuiFlexItem>

              {/* Message */}
              <EuiFlexItem>
                <EuiText size="s">
                  <p className={classes.message} style={{ marginBottom: "4px" }}>
                    {i.message}
                  </p>
                </EuiText>
              </EuiFlexItem>

              {/* Priority */}
              <EuiFlexItem>
                <EuiFlexGroup gutterSize="m" alignItems="center">
                  <EuiFlexItem grow={false}>
                    <EuiText size="xs">
                      <p style={{ color: "#7d7e80" }}>
                        Priority: <span style={{ color: iconProps.color }}>{i.priority}</span>
                      </p>
                    </EuiText>
                  </EuiFlexItem>
                  <EuiFlexItem grow={false}>
                    <Button
                      type="empty"
                      text={"Mark as Read"}
                      onClick={() => markNotificationAsRead(i.id, idx)}
                      style={{ fontSize: "var(--fs12)" }}
                    />
                  </EuiFlexItem>
                </EuiFlexGroup>
              </EuiFlexItem>
            </EuiFlexGroup>
          </EuiFlexItem>

          <EuiFlexItem grow={false}>
            <EuiFlexGroup gutterSize="s" alignItems="center">
              {/* Read/Unread indicator - clickable only if unread */}
              <EuiFlexItem grow={false}>
                {i?.attributes?.isRead ? (
                  <div style={{ cursor: "default" }}>
                    <EuiIcon type="checkInCircleFilled" color="#14a059" size="m" />
                  </div>
                ) : (
                  <EuiToolTip content="Mark as read">
                    <div
                      role="button"
                      aria-label="Mark notification as read"
                      onClick={() => markNotificationAsRead(i.id, idx)}
                      style={{ cursor: "pointer" }}>
                      <EuiIcon type="checkCircle" color="#006BB4" size="m" />
                    </div>
                  </EuiToolTip>
                )}
              </EuiFlexItem>
              {/* Delete button */}
              <EuiFlexItem grow={false}>
                <IconButton
                  type="trash"
                  onClick={() => deleteNotification(i.id)}
                  iconSize="m"
                  size="m"
                />
              </EuiFlexItem>
            </EuiFlexGroup>
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFlexItem>
    );
  };

  const markNotificationAsRead = async (notificationId: number, index: number) => {
    try {
      if (clientId) {
        setNotifications((prevNotifications) =>
          prevNotifications.map((notification) =>
            notification.id === notificationId
              ? { ...notification, attributes: { ...notification.attributes, isRead: true } }
              : notification
          )
        );

        await ClientService.markUserNotificationAsRead(clientId, notificationId);
      }
    } catch (error) {
      console.error("Failed to mark notification as read:", error);

      setNotifications((prevNotifications) =>
        prevNotifications.map((notification) =>
          notification.id === notificationId
            ? { ...notification, attributes: { ...notification.attributes, isRead: false } }
            : notification
        )
      );
    }
  };

  const deleteNotification = async (notificationId: number) => {
    try {
      if (clientId) {
        setNotifications((prevNotifications) =>
          prevNotifications.filter((notification) => notification.id !== notificationId)
        );

        await ClientService.deleteUserNotification(clientId, notificationId);
      }
    } catch (error) {
      console.error("Failed to delete notification:", error);

      const notificationToRestore = notifications.find((n) => n.id === notificationId);
      if (notificationToRestore) {
        setNotifications((prevNotifications) => {
          const newNotifications = [...prevNotifications, notificationToRestore];
          return newNotifications.sort(
            (a, b) => moment(b.createDateTime).valueOf() - moment(a.createDateTime).valueOf()
          );
        });
      }
    }
  };

  return (
    <Flyout
      title="Notifications"
      size="600px"
      onClose={onClose}
      tabs={
        <EuiTabs
          style={{
            borderBottom: "1px solid var(--border-color)",
          }}>
          {renderTabs}
        </EuiTabs>
      }
      children={
        <>
          {selectedTabId && (
            <>
              {selectedTabId === tabs[0].id && (
                <EuiFlexGroup direction="column" gutterSize="xl">
                  {notifications.map((i, idx) => renderNotification(i, idx))}
                  {notifications?.length === 0 && noNotifications}
                </EuiFlexGroup>
              )}

              {selectedTabId === tabs[1].id && (
                <EuiFlexGroup direction="column" gutterSize="l">
                  {unread.map((i, idx) => renderNotification(i, idx))}
                  {unread?.length === 0 && noNotifications}
                </EuiFlexGroup>
              )}
            </>
          )}
        </>
      }
    />
  );
};
